import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../img/logo512.png';
import './Navbar.css';
import useWindowWidth from '../../utils/useWindowWidth';
import { toFriendlyTon } from '../../utils/tonFriendly';

const COLOR_PRESETS = [
    { hex: '#22c55e', title: 'Green — default' },
    { hex: '#ef4444', title: 'Red — suspicious' },
    { hex: '#f59e0b', title: 'Amber — watch' },
    { hex: '#3b82f6', title: 'Blue — known' },
    { hex: '#a855f7', title: 'Purple — tagged' },
    { hex: '#ec4899', title: 'Pink' },
    { hex: '#14b8a6', title: 'Teal' },
    { hex: '#ffffff', title: 'White — highlight' },
];

function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return {
        r: parseInt(h.substring(0, 2), 16) || 0,
        g: parseInt(h.substring(2, 4), 16) || 0,
        b: parseInt(h.substring(4, 6), 16) || 0,
    };
}

function rgbToHex(r, g, b) {
    const c = (v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, '0');
    return `#${c(r)}${c(g)}${c(b)}`;
}

function getAddressUrl(network, address) {
    switch (network) {
    case 'Ethereum':
        return `https://etherscan.io/address/${address}`;
    case 'Polygon':
        return `https://polygonscan.com/address/${address}`;
    case 'BSC':
        return `https://bscscan.com/address/${address}`;
    case 'Solana':
        return `https://explorer.solana.com/address/${address}`;
    case 'TON':
        return `https://tonviewer.com/${address}`;
    case 'TON Testnet':
        return `https://testnet.tonviewer.com/${address}`;
    default:
        return '#';
    }
}

function shortAddr(addr, width, keep = 6) {
    if (!addr) {
        return '';
    }
    const k = width < 768 ? keep : 10;
    if (addr.length > 2 * k) {
        return `${addr.slice(0, k)}…${addr.slice(-k)}`;
    }
    return addr;
}

function friendlyAddr(addr, allTransactions) {
    if (!addr) {
        return addr;
    }
    const bucket = (allTransactions || []).find((tx) =>
        (tx.transactions || []).some(
            (t) =>
                t.from?.toLowerCase() === addr.toLowerCase() ||
                t.to?.toLowerCase() === addr.toLowerCase(),
        ),
    );
    const net = bucket?.network;
    if (net === 'TON' || net === 'TON Testnet') {
        return toFriendlyTon(addr, { testOnly: net === 'TON Testnet', bounceable: false });
    }
    return addr;
}

function AddressModal({
    isOpen,
    onClose,
    selectedAddress,
    allTransactions,
    onLoadTx,
    nodeAnnotations,
    onSaveAnnotation,
    heatmapOn,
}) {
    const modalRef = useRef(null);
    const [pos, setPos] = useState(null);
    const draggingRef = useRef(false);
    const startRef = useRef({ x: 0, y: 0, left: 0, top: 0 });
    const rafRef = useRef(null);
    const nextPosRef = useRef(null);
    const movedRef = useRef(false);
    const THRESHOLD = 3;

    // ── Local draft state for color & note ──────────────────
    const [draftColor, setDraftColor] = useState(null);
    const [draftNote, setDraftNote]   = useState('');

    // Reset draft when modal opens with a new address
    useEffect(() => {
        if (isOpen && selectedAddress) {
            const existing = nodeAnnotations?.get(selectedAddress);
            setDraftColor(existing?.color || null);
            setDraftNote(existing?.note || '');
        }
    }, [isOpen, selectedAddress, nodeAnnotations]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const onKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isOpen, onClose]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const prevBodyOverflow = document.body.style.overflow;
        const prevHtmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevBodyOverflow;
            document.documentElement.style.overflow = prevHtmlOverflow;
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            setPos(null);
        }
    }, [isOpen]);

    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

    const computeClamped = (rawX, rawY) => {
        const el = modalRef.current;
        if (!el) {
            return { x: rawX, y: rawY };
        }
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const m = 8;
        return {
            x: clamp(rawX, m, Math.max(m, vw - rect.width - m)),
            y: clamp(rawY, m, Math.max(m, vh - rect.height - m)),
        };
    };

    const scheduleRaf = () => {
        if (rafRef.current) {
            return;
        }
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            if (nextPosRef.current) {
                setPos(nextPosRef.current);
                nextPosRef.current = null;
            }
        });
    };

    const startDrag = (e) => {
        if (e.button !== 0) {
            return;
        }
        const interactiveTags = ['BUTTON', 'A', 'INPUT', 'TEXTAREA', 'SELECT', 'LABEL'];
        if (interactiveTags.includes(e.target.tagName) || e.target.closest('[data-nodrag]')) {
            return;
        }

        e.stopPropagation();
        e.currentTarget.setPointerCapture?.(e.pointerId);

        const rect = modalRef.current?.getBoundingClientRect();
        const left = pos?.x ?? rect.left;
        const top = pos?.y ?? rect.top;

        startRef.current = { x: e.clientX, y: e.clientY, left, top };
        draggingRef.current = true;
        movedRef.current = false;
        document.body.classList.add('dragging-modal');
    };

    const moveDrag = (e) => {
        if (!draggingRef.current) {
            return;
        }
        const dx = e.clientX - startRef.current.x;
        const dy = e.clientY - startRef.current.y;
        if (!movedRef.current && (Math.abs(dx) > THRESHOLD || Math.abs(dy) > THRESHOLD)) {
            movedRef.current = true;
        }
        const raw = { x: startRef.current.left + dx, y: startRef.current.top + dy };
        nextPosRef.current = computeClamped(raw.x, raw.y);
        scheduleRaf();
    };

    const endDrag = (e) => {
        if (!draggingRef.current) {
            return;
        }
        draggingRef.current = false;
        document.body.classList.remove('dragging-modal');
        e.currentTarget?.releasePointerCapture?.(e.pointerId);
    };

    const onBackdropMouseDown = () => {
        if (movedRef.current) {
            return;
        }
        onClose();
    };

    if (!isOpen || !selectedAddress) {
        return null;
    }

    const txBucket = allTransactions.find((tx) =>
        (tx.transactions || []).some(
            (t) =>
                t.from?.toLowerCase() === selectedAddress.toLowerCase() ||
                t.to?.toLowerCase() === selectedAddress.toLowerCase(),
        ),
    );
    const network = txBucket?.network ?? 'Unknown';

    const copyAddr = async () => {
        try {
            await navigator.clipboard.writeText(friendly);
        } catch (err) {
            console.debug('Clipboard write failed:', err);
        }
    };

    const handleSave = () => {
        if (onSaveAnnotation) {
            onSaveAnnotation(selectedAddress, {
                color: draftColor,
                note: draftNote.trim(),
            });
        }
        onClose();
    };

    const modalStyle = pos ? { left: pos.x, top: pos.y } : undefined;
    const modalClass = `modal${pos ? ' dragging grabbing' : ''}`;

    const friendly =
        network === 'TON' || network === 'TON Testnet'
            ? toFriendlyTon(selectedAddress, { testOnly: network === 'TON Testnet', bounceable: false })
            : selectedAddress;

    const explorerUrl = network === 'Unknown' ? null : getAddressUrl(network, friendly);

    return (
        <div className="modal-backdrop" onMouseDown={onBackdropMouseDown}>
            <div
                ref={modalRef}
                className={modalClass}
                role="dialog"
                aria-modal="true"
                aria-labelledby="addr-modal-title"
                onMouseDown={(e) => e.stopPropagation()}
                style={modalStyle}
            >
                <div
                    className="modal-header"
                    onPointerDown={startDrag}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                >
                    <h3 id="addr-modal-title">Selected address</h3>
                    <button
                        className="icon-btn"
                        aria-label="Close"
                        data-nodrag
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={onClose}
                    >
                        ✕
                    </button>
                </div>

                <div className="modal-body">
                    <div className="kv">
                        <span className="k">Address</span>
                        <div className="addr-line">
                            {explorerUrl ? (
                                <a
                                    className="addr-text link"
                                    href={explorerUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={friendly}
                                >
                                    {friendly}
                                </a>
                            ) : (
                                <span className="addr-text" title={friendly}>
                                    {friendly}
                                </span>
                            )}
                            <button className="mini-btn" onClick={copyAddr} title="Copy">
                                ⧉
                            </button>
                            {explorerUrl && (
                                <a
                                    className="mini-btn"
                                    href={explorerUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="Open in explorer"
                                >
                                    ↗
                                </a>
                            )}
                        </div>
                    </div>
                    <div className="kv">
                        <span className="k">Network</span>
                        <span className="v">{network}</span>
                    </div>

                    {/* ── Color picker ──────────────────────────── */}
                    <div className="kv kv-top">
                        <span className="k">Color</span>
                        <div className={`color-section${heatmapOn ? ' disabled' : ''}`}>
                            <div className="color-presets">
                                {COLOR_PRESETS.map(({ hex, title }) => (
                                    <button
                                        key={hex}
                                        className={`color-dot${draftColor === hex ? ' active' : ''}`}
                                        style={{ background: hex }}
                                        title={heatmapOn ? 'Disabled in heatmap mode' : title}
                                        disabled={heatmapOn}
                                        onClick={() => setDraftColor(draftColor === hex ? null : hex)}
                                    />
                                ))}
                            </div>
                            <div className="rgb-picker">
                                <div
                                    className="rgb-preview"
                                    style={{ background: draftColor || '#22c55e' }}
                                />
                                <div className="rgb-sliders">
                                    <label className="rgb-row" data-nodrag>
                                        <span className="rgb-label" style={{ color: '#f87171' }}>R</span>
                                        <input
                                            type="range" min="0" max="255"
                                            value={hexToRgb(draftColor || '#22c55e').r}
                                            disabled={heatmapOn}
                                            data-nodrag
                                            onChange={(e) => {
                                                const c = hexToRgb(draftColor || '#22c55e');
                                                setDraftColor(rgbToHex(Number(e.target.value), c.g, c.b));
                                            }}
                                        />
                                        <span className="rgb-val">{hexToRgb(draftColor || '#22c55e').r}</span>
                                    </label>
                                    <label className="rgb-row" data-nodrag>
                                        <span className="rgb-label" style={{ color: '#4ade80' }}>G</span>
                                        <input
                                            type="range" min="0" max="255"
                                            value={hexToRgb(draftColor || '#22c55e').g}
                                            disabled={heatmapOn}
                                            data-nodrag
                                            onChange={(e) => {
                                                const c = hexToRgb(draftColor || '#22c55e');
                                                setDraftColor(rgbToHex(c.r, Number(e.target.value), c.b));
                                            }}
                                        />
                                        <span className="rgb-val">{hexToRgb(draftColor || '#22c55e').g}</span>
                                    </label>
                                    <label className="rgb-row" data-nodrag>
                                        <span className="rgb-label" style={{ color: '#60a5fa' }}>B</span>
                                        <input
                                            type="range" min="0" max="255"
                                            value={hexToRgb(draftColor || '#22c55e').b}
                                            disabled={heatmapOn}
                                            data-nodrag
                                            onChange={(e) => {
                                                const c = hexToRgb(draftColor || '#22c55e');
                                                setDraftColor(rgbToHex(c.r, c.g, Number(e.target.value)));
                                            }}
                                        />
                                        <span className="rgb-val">{hexToRgb(draftColor || '#22c55e').b}</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Note ──────────────────────────────────── */}
                    <div className="kv kv-top">
                        <span className="k">Note</span>
                        <textarea
                            className="note-input"
                            rows="2"
                            placeholder="Add a note about this address…"
                            value={draftNote}
                            onChange={(e) => setDraftNote(e.target.value)}
                            data-nodrag
                        />
                    </div>
                </div>

                <div className="modal-actions">
                    {network !== 'Unknown' && (
                        <button className="btn" onClick={() => onLoadTx(selectedAddress, network)}>
                            Load transactions
                        </button>
                    )}
                    <button className="btn" onClick={handleSave}>
                        Save
                    </button>
                    <button className="btn ghost" onClick={onClose}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

const Navbar = ({
    selectedAddress,
    allTransactions,
    loadNewTransactions,
    toggleAnimation,
    isAnimating,
    toggleHeatmap,
    heatmapOn,
    setIsGraphLoading,
    isModalOpen,
    setIsModalOpen,
    nodeAnnotations,
    onSaveAnnotation,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const isScanPage = location.pathname === '/scan';
    const width = useWindowWidth();

    useEffect(() => {
        const prevPadding = document.body.style.paddingTop;
        const h =
            getComputedStyle(document.documentElement).getPropertyValue('--navbar-h') || '56px';
        document.body.style.paddingTop = h.trim();
        return () => {
            document.body.style.paddingTop = prevPadding;
        };
    }, []);

    const handleLogoClick = () => {
        const qs = new URLSearchParams(location.search).toString();
        navigate(`/?${qs}`);
    };

    const handleFullscreen = () => {
        if (!document.fullscreenElement) {
            document.documentElement
                .requestFullscreen()
                .catch((err) => console.error('Fullscreen error:', err));
        } else {
            document.exitFullscreen().catch((err) => console.error('Exit fullscreen error:', err));
        }
    };

    const handleReload = () => {
        window.location.reload();
    };

    const handleLoadTransactions = (addr, network) => {
        if (setIsGraphLoading) {
            setIsGraphLoading(true);
        }
        if (loadNewTransactions) {
            loadNewTransactions(addr, network);
        }
        if (setIsModalOpen) {
            setIsModalOpen(false);
        }
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.target && ['INPUT', 'TEXTAREA'].includes(e.target.tagName)) {
                return;
            }
            const k = e.key.toLowerCase();
            if (k === 'f') {
                handleFullscreen();
            }
            if (k === 'a') {
                toggleAnimation?.();
            }
            if (k === 'h') {
                toggleHeatmap?.();
            }
            if (k === 'r') {
                handleReload();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [toggleAnimation, toggleHeatmap]);

    return (
        <nav className="navbar" role="navigation" aria-label="Main">
            <button className="brand" onClick={handleLogoClick} aria-label="Home">
                <img src={Logo} alt="Logo" />
                <span className="brand-title">Analcha.in</span>
            </button>

            {isScanPage && (
                <div className="toolbar" aria-label="Graph controls">
                    <button
                        className="icon-btn"
                        title="Fullscreen (F)"
                        aria-label="Fullscreen"
                        onClick={handleFullscreen}
                    >
                        ⛶
                    </button>
                    <button
                        className="icon-btn"
                        title="Toggle animation (A)"
                        aria-label="Toggle animation"
                        onClick={toggleAnimation}
                    >
                        {isAnimating ? '⏸' : '▶︎'}
                    </button>
                    <button
                        className={`icon-btn${heatmapOn ? ' active' : ''}`}
                        title="Heatmap (H)"
                        aria-label="Toggle heatmap"
                        onClick={toggleHeatmap}
                    >
                        🌡
                    </button>
                    <button
                        className="icon-btn"
                        title="Reload (R)"
                        aria-label="Reload"
                        onClick={handleReload}
                    >
                        ↻
                    </button>
                </div>
            )}

            {selectedAddress && (
                <div
                    className="selected-pill"
                    onClick={() => {
                        if (setIsModalOpen) {
                            setIsModalOpen(true);
                        }
                    }}
                    title="Open details"
                >
                    {shortAddr(friendlyAddr(selectedAddress, allTransactions), width)}
                </div>
            )}

            <AddressModal
                isOpen={isModalOpen}
                onClose={() => {
                    if (setIsModalOpen) {
                        setIsModalOpen(false);
                    }
                }}
                selectedAddress={selectedAddress}
                allTransactions={allTransactions}
                onLoadTx={handleLoadTransactions}
                nodeAnnotations={nodeAnnotations}
                onSaveAnnotation={onSaveAnnotation}
                heatmapOn={heatmapOn}
            />
        </nav>
    );
};

export default Navbar;